import { type ColumnDef } from '@tanstack/react-table'
import { MoneyCell, MutedCell, NumberCell } from './TableCells'
import { optionalColumn } from '@/components/ui/data-table'
import { formatNumber, formatPercent } from '../utils/format'
import { formatMoney } from '../utils/money'
import type { StatLabel } from '../hooks/useStatLabel'

/**
 * How a metric is rendered in a stats column. `percent` is for values already
 * scaled to 0-100; `ratio` is for the 0-1 fractions the stats API reports its
 * own rates as (ctr, viewability, renderedRate).
 */
export type StatMetricFormat = 'count' | 'percent' | 'ratio' | 'decimal' | 'currency'

/**
 * The family a metric belongs to — traffic asked for, what was delivered of it
 * and to how many, what people did with it, what it was paid for and what that
 * works out to per thousand. Lists of metrics are read a group at a time.
 */
export type StatMetricGroup = 'traffic' | 'delivery' | 'engagement' | 'money' | 'rates'

/** Groups in reading order, with the heading each is shown under. */
export const STAT_METRIC_GROUPS: { key: StatMetricGroup; labelKey: string }[] = [
  { key: 'traffic', labelKey: 'stats.groupTraffic' },
  { key: 'delivery', labelKey: 'stats.groupDelivery' },
  { key: 'engagement', labelKey: 'stats.groupEngagement' },
  { key: 'money', labelKey: 'stats.groupMoney' },
  { key: 'rates', labelKey: 'stats.groupRates' },
]

export interface StatMetric {
  /** Field name in the stats chunk. */
  key: string
  labelKey: string
  format: StatMetricFormat
  group?: StatMetricGroup
  /** Shown without having to pick it in the column picker. */
  defaultVisible?: boolean
}

/**
 * Every metric the stats API reports, with the label and shape each is read
 * in. One catalog so a metric means the same thing in a column, a tile and the
 * all-stats list — and so a new metric only has to be described once.
 */
export const STAT_METRIC_CATALOG: Record<string, Omit<StatMetric, 'key'>> = {
  requests: { labelKey: 'stats.requests', format: 'count', group: 'traffic' },
  bids: { labelKey: 'stats.availableTraffic', format: 'count', group: 'traffic' },
  impressionRequests: { labelKey: 'stats.impressionRequests', format: 'count', group: 'traffic' },
  positiveImpressionRequests: {
    labelKey: 'stats.positiveImpressionRequests',
    format: 'count',
    group: 'traffic',
  },
  matchRate: { labelKey: 'stats.matchRate', format: 'ratio', group: 'traffic' },
  internalBidsRejected: {
    labelKey: 'stats.internalBidsRejected',
    format: 'count',
    group: 'traffic',
  },
  // Traffic a line item could have bid on but did not, by why it was filtered.
  frequencyCapped: { labelKey: 'stats.frequencyCapped', format: 'count', group: 'traffic' },
  rateLimited: { labelKey: 'stats.rateLimited', format: 'count', group: 'traffic' },
  belowFloor: { labelKey: 'stats.belowFloor', format: 'count', group: 'traffic' },
  losingBids: { labelKey: 'stats.losingBids', format: 'count', group: 'traffic' },
  deviceStorageAllowed: {
    labelKey: 'stats.deviceStorageAllowed',
    format: 'count',
    group: 'traffic',
  },

  impressions: { labelKey: 'stats.impressions', format: 'count', group: 'delivery' },
  rendered: { labelKey: 'stats.rendered', format: 'count', group: 'delivery' },
  renderedRate: { labelKey: 'stats.renderedRate', format: 'ratio', group: 'delivery' },
  viewables: { labelKey: 'stats.viewables', format: 'count', group: 'delivery' },
  viewability: { labelKey: 'stats.viewability', format: 'ratio', group: 'delivery' },
  fullyViewables: { labelKey: 'stats.fullyViewables', format: 'count', group: 'delivery' },
  visibles: { labelKey: 'stats.visibles', format: 'count', group: 'delivery' },
  visibility: { labelKey: 'stats.visibility', format: 'ratio', group: 'delivery' },
  averageRelativeSize: {
    labelKey: 'stats.averageRelativeSize',
    format: 'decimal',
    group: 'delivery',
  },
  averageAdDensity: { labelKey: 'stats.averageAdDensity', format: 'ratio', group: 'delivery' },
  uniqueUsers: { labelKey: 'stats.uniqueUsers', format: 'count', group: 'delivery' },
  impressionsPerUniqueUser: {
    labelKey: 'stats.impressionsPerUser',
    format: 'decimal',
    group: 'delivery',
  },

  clicks: { labelKey: 'stats.clicks', format: 'count', group: 'engagement' },
  ctr: { labelKey: 'stats.ctr', format: 'ratio', group: 'engagement' },
  conversions: { labelKey: 'stats.conversions', format: 'count', group: 'engagement' },

  // Cost, then each fee on top of it, then the total they come to.
  cost: { labelKey: 'stats.cost', format: 'currency', group: 'money' },
  fee: { labelKey: 'stats.fee', format: 'currency', group: 'money' },
  techFee: { labelKey: 'stats.techFee', format: 'currency', group: 'money' },
  dataFee: { labelKey: 'stats.dataFee', format: 'currency', group: 'money' },
  creativeFormatFee: { labelKey: 'stats.creativeFormatFee', format: 'currency', group: 'money' },
  creativeTagFee: { labelKey: 'stats.creativeTagFee', format: 'currency', group: 'money' },
  supplementaryFee: { labelKey: 'stats.supplementaryFee', format: 'currency', group: 'money' },
  totalCost: { labelKey: 'stats.totalCost', format: 'currency', group: 'money' },
  earnings: { labelKey: 'stats.earnings', format: 'currency', group: 'money' },
  revenueShare: { labelKey: 'stats.revenueShare', format: 'currency', group: 'money' },

  eCpm: { labelKey: 'stats.ecpm', format: 'currency', group: 'rates' },
  grossECpm: { labelKey: 'stats.grossEcpm', format: 'currency', group: 'rates' },
  vCpm: { labelKey: 'stats.vcpm', format: 'currency', group: 'rates' },
  grossVCpm: { labelKey: 'stats.grossVcpm', format: 'currency', group: 'rates' },
  cpc: { labelKey: 'stats.cpc', format: 'currency', group: 'rates' },
  grossCpc: { labelKey: 'stats.grossCpc', format: 'currency', group: 'rates' },
  averageAuctionRank: { labelKey: 'stats.averageAuctionRank', format: 'decimal', group: 'rates' },
}

/**
 * Picks metrics out of the catalog for a table — `visible` ones are shown,
 * `rest` are offered in the column picker.
 */
export function catalogMetrics(visible: string[], rest: string[] = []): StatMetric[] {
  const of = (key: string, defaultVisible: boolean): StatMetric => ({
    key,
    defaultVisible,
    ...STAT_METRIC_CATALOG[key],
  })
  return [...visible.map((k) => of(k, true)), ...rest.map((k) => of(k, false))]
}

/** One group's metrics, keyed by metric name as the API reports them. */
export type StatsRow = Record<string, unknown>

/** Chunk values are either plain numbers or `{ currency, amount }`. */
export function numericValue(value: unknown): number | null {
  if (typeof value === 'number') return value
  if (value && typeof value === 'object' && 'amount' in value) {
    return (value as { amount: number }).amount ?? null
  }
  return null
}

/** Is this one of the `{ amount, currency }` pairs the API reports money as? */
export function isCurrencyAmount(value: unknown): value is { amount: number; currency: string } {
  return !!value && typeof value === 'object' && typeof (value as { amount?: unknown }).amount === 'number'
}

/**
 * A metric value as text, in the user's locale. Null when there is nothing to
 * show, which every caller renders as a dash of its own.
 */
export function formatStatValue(value: unknown, format: StatMetricFormat): string | null {
  if (value === undefined || value === null) return null
  if (isCurrencyAmount(value)) return formatMoney(value.amount, value.currency, 2)
  const num = numericValue(value)
  if (num === null) return null
  if (format === 'percent' || format === 'ratio') return formatPercent(format === 'ratio' ? num * 100 : num)
  if (format === 'decimal') {
    return formatNumber(num, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  return formatNumber(num)
}

function StatCell({ metric, value }: { metric: StatMetric; value: unknown }) {
  if (value === undefined || value === null) return <MutedCell />
  if (metric.format === 'currency') {
    return <MoneyCell value={isCurrencyAmount(value) ? value : null} />
  }
  const num = numericValue(value)
  if (num === null) return <MutedCell />
  if (metric.format === 'count') return <NumberCell value={num} />
  return (
    <span className="text-xs tabular-nums text-(--ad-text)">
      {formatStatValue(value, metric.format)}
    </span>
  )
}

interface StatColumnsOptions<TData> {
  metrics: StatMetric[]
  /** Metrics for the row, or undefined when it has no delivery in the period. */
  statsFor: (row: TData) => StatsRow | undefined
  /** Names a metric — from `useStatLabel`, so it reads as this network words it. */
  label: StatLabel
  /** Totals across every row, shown in a footer under each metric. */
  totals?: StatsRow
  /** Groups the columns under their own heading in the column picker. */
  section?: string
}

/**
 * Turns metric definitions into sortable table columns. Metrics not flagged
 * `defaultVisible` are offered in the column picker instead of shown.
 */
export function statMetricColumns<TData>({
  metrics,
  statsFor,
  label,
  totals,
  section,
}: StatColumnsOptions<TData>): ColumnDef<TData, unknown>[] {
  return metrics.map((metric) => {
    const column: ColumnDef<TData, unknown> = {
      id: `stat_${metric.key}`,
      header: label(metric.labelKey),
      size: 110,
      // Figures read right, so the digits line up down the column.
      meta: {
        label: label(metric.labelKey),
        align: 'right',
        ...(section ? { section } : {}),
        ...(totals ? { exportTotal: numericValue(totals[metric.key]) } : {}),
      },
      // Sort on the raw number so currency amounts order by value, not by label.
      accessorFn: (row: TData) => numericValue(statsFor(row)?.[metric.key]) ?? 0,
      cell: ({ row }) => <StatCell metric={metric} value={statsFor(row.original)?.[metric.key]} />,
      ...(totals ? { footer: () => <StatCell metric={metric} value={totals[metric.key]} /> } : {}),
    }
    return metric.defaultVisible ? column : optionalColumn<TData>(column)
  })
}
